<template>
  <v-row v-if="filteredCompatibilities.length" class="compatibilities-section">
    <v-col cols="12">
      <h3>Compatibilities</h3>
      <div v-if="isLoading" class="text-center pa-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading compatibilities...</p>
      </div>
      <v-row v-else>
        <v-col
          v-for="compatibility in filteredCompatibilities"
          :key="compatibility.id"
          cols="4"
          class="checkbox-column"
        >
          <v-checkbox
            :model-value="isCompatibilitySelected(compatibility)"
            :label="compatibility.name"
            dense
            class="compact-checkbox"
            @update:model-value="(checked) => toggleCompatibility(compatibility, checked)"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import apiService from "@/services/apiService";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  groupSlug: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue"]);

const store = useStore();
const route = useRoute();

const allCompatibilities = ref([]);
const isLoading = ref(false);

// Fetch compatibilities from the backend
const fetchCompatibilities = async () => {
  try {
    isLoading.value = true;
    const response = await apiService.get("/compatibilities");
    
    // Handle the nested response structure
    const responseData = response.data?.data || response.data || [];
    allCompatibilities.value = Array.isArray(responseData) ? responseData : [];
    
    console.log("✅ Compatibilities fetched:", {
      count: allCompatibilities.value.length,
      data: allCompatibilities.value
    });
  } catch (error) {
    console.error("❌ Failed to fetch compatibilities:", error);
    allCompatibilities.value = [];
  } finally {
    isLoading.value = false;
  }
};

const filteredCompatibilities = computed(() => {
  // Get the selected group from store
  const selectedGroup = store.getters["NewCategory/selectedGroup"];
  const subcategorySlug = route.params.subcategorySlug;
  
  if (!selectedGroup || !allCompatibilities.value.length) {
    console.log("⚠️ No selectedGroup or compatibilities available:", {
      selectedGroup,
      allCompatibilitiesLength: allCompatibilities.value.length
    });
    return [];
  }

  // Only show compatibilities for auto-accessories subcategory
  if (subcategorySlug !== "auto-accessories") {
    console.log("⚠️ Not auto-accessories subcategory:", subcategorySlug);
    return [];
  }

  // Get the group slug from the selected group
  const groupSlug = selectedGroup.slug;
  
  // Define which compatibilities should show for each group
  const groupCompatibilityMapping = {
    "motorcycle-parts": [
      "All Motorcycles", "Ducati", "KTM", "BMW Motorcycle", "Bajaj", "Triumph",
      "Harley Davidson", "Honda Motorcycle", "Kawasaki", "Yamaha", 
      "Suzuki Motorcycle", "Other Motorcycle Manufacturer"
    ],
    "boat-parts": [
      "Inboard Motor Boats", "Sailboats", "Outboard Motor Boats", "All Boat Type",
      "Row/Paddle Boats", "Other Boat Type"
    ]
  };

  // Check if this group should show compatibilities
  if (!groupCompatibilityMapping[groupSlug]) {
    console.log("⚠️ No compatibilities defined for group:", {
      groupSlug,
      groupId: selectedGroup.id,
      groupName: selectedGroup.name || selectedGroup.title
    });
    return [];
  }

  // Get the list of compatible names for this group
  const compatibleNames = groupCompatibilityMapping[groupSlug];

  // Filter based on compatibility names
  const filtered = allCompatibilities.value.filter((compatibility) => {
    return compatibleNames.includes(compatibility.name);
  });

  console.log("🔍 Debugging filteredCompatibilities:", {
    selectedGroup: {
      id: selectedGroup.id,
      name: selectedGroup.name || selectedGroup.title,
      slug: selectedGroup.slug
    },
    subcategorySlug,
    totalCompatibilities: allCompatibilities.value.length,
    compatibleNames,
    filteredCount: filtered.length,
    filtered: filtered.map(c => c.name)
  });

  return filtered;
});

// Track selected compatibility IDs
const selectedCompatibilityIds = computed(() => {
  return props.modelValue.map((item) => item.id).filter(Boolean);
});

// Check if a specific compatibility is selected
const isCompatibilitySelected = (compatibility) => {
  return selectedCompatibilityIds.value.includes(compatibility.id);
};

// Toggle compatibility selection
// Update the toggleCompatibility function:
const toggleCompatibility = (compatibility, checked) => {
  const currentValue = [...props.modelValue];

  if (checked) {
    const exists = currentValue.some((item) => item.id === compatibility.id);
    if (!exists) {
      currentValue.push({
        id: compatibility.id,
        name: compatibility.name,
        is_compatible: true, // Default to true when selected
      });
    }
  } else {
    const filteredValue = currentValue.filter((item) => item.id !== compatibility.id);
    emit("update:modelValue", filteredValue);
    return;
  }

  emit("update:modelValue", currentValue);
};
// Fetch compatibilities on mount
onMounted(() => {
  fetchCompatibilities();
  
  const selectedGroup = store.getters["NewCategory/selectedGroup"];
  console.log("🔍 CompatibilitiesCheckBox mounted with:", {
    selectedGroup,
    routeParams: route.params,
    propsGroupSlug: props.groupSlug
  });
});
</script>
<style scoped>
.compatibilities-section {
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 0;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Title styling */
h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.87);
  padding-left: 4px;
}

/* Column styling for 3 items per row */
.checkbox-column {
  padding: 2px 4px !important;
}

/* Compact checkbox styling */
.compact-checkbox {
  margin-bottom: 4px !important;
  font-size: 0.875rem;
  padding: 0 !important;
}

/* Remove all form styling from compatibilities */
:deep(.compatibilities-section .v-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.compatibilities-section .v-field) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  min-height: auto !important;
  padding: 0 !important;
}

:deep(.compatibilities-section .v-field__input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-height: auto !important;
}

/* Checkbox specific styling */
:deep(.compact-checkbox .v-input__control) {
  min-height: auto !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.compact-checkbox .v-field) {
  min-height: auto !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  align-items: flex-start !important;
}

:deep(.compact-checkbox .v-field__input) {
  min-height: auto !important;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* Checkbox label styling */
:deep(.compact-checkbox .v-label) {
  font-size: 0.875rem !important;
  color: rgba(0, 0, 0, 0.87) !important;
  line-height: 1.2 !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  background: transparent !important;
  padding: 0 !important;
  margin-left: 8px !important;
}

/* Checkbox input styling */
:deep(.compact-checkbox .v-selection-control__input) {
  margin-right: 8px !important;
}

/* Remove any hover effects that might add borders */
:deep(.compatibilities-section .v-input:hover),
:deep(.compatibilities-section .v-field:hover),
:deep(.compatibilities-section .v-input--focused),
:deep(.compatibilities-section .v-field--focused) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* Responsive design for mobile */
@media (max-width: 768px) {
  .checkbox-column {
    padding: 1px 2px !important;
  }
  
  h3 {
    font-size: 1rem;
    margin-bottom: 8px;
  }
  
  :deep(.compact-checkbox .v-label) {
    font-size: 0.8rem !important;
    line-height: 1.1 !important;
  }
}

@media (max-width: 600px) {
  /* On very small screens, might want to go back to 2 columns */
  .checkbox-column {
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }
}
</style>